/** Formatting. The **only** place a quantity becomes display text.
 *
 * `CLAUDE.md` rule 5: every amount crosses the wire as a decimal string, never a JSON number,
 * and rounding happens in the presentation layer only. So this module is that layer, and the
 * rule it enforces is that a value is never converted to `number` before it has been rounded
 * for display.
 *
 * Why that matters concretely: `Number("1629851.4000000000")` is fine, but the habit of parsing
 * first and formatting later is how a float creeps into a figure a customer files. Keeping the
 * parse inside these functions means a screen cannot do arithmetic on an amount by accident —
 * the generated types say `string`, and nothing here hands back a number.
 *
 * **The locale is named, and that is the whole of EXP-39's "regional units and dates" (display
 * half).** It is `en-GB` everywhere, never `undefined` — and `undefined` is the trap, because it
 * means *the reader's browser*. A SECR total is `1,629,851.4` here and `1.629.851,4` to somebody
 * whose browser is set to German, so one filed figure acquires two renderings inside one product,
 * and a reader comparing the Reports preview against the register sees 1.6 thousand where the
 * other screen says 1.6 million. It happened: `Reports.tsx` had its own `amount()` doing exactly
 * that, which also made this module's claim to be the only formatter false.
 *
 * `en-GB` rather than a per-market choice is deliberate. Both shipping markets group in thousands
 * with a comma and separate decimals with a point, so the two agree on every figure this product
 * states — and a *date* is where they diverge, which is why `shortDate` never writes one in
 * digits. A locale switch would therefore change nothing a UK or Californian reader sees and would
 * reintroduce the divergence for everybody else.
 *
 * **That paragraph is still true of numbers and of calendar dates, and gate 30 added one thing
 * beside it:** a `Reader` (see below) for *instants* - a time zone, and a 24-hour or 12-hour clock.
 * No function that writes a quantity takes one, because the two offered locales write every
 * quantity identically; a parameter that could not change its output would be a setting tested
 * against itself.
 *
 * **Nothing exported from here is used in an export file.** `register.ts` writes the wire's own
 * decimal strings into a CSV: a spreadsheet given `1,629,851.4` reads one cell as text, or two.
 */

import { perThousand, roundDecimal, roundSignificant, sumDecimals } from "./decimal";

/** Kilograms to tonnes, rounded for display at a stated precision.
 *
 * Emissions are stored in kg because factors are published per kg, and shown in tonnes because
 * that is the unit both regimes report in. The division happens here, once.
 */
export function tonnes(kg: string, decimals = 1): string {
  const exact = perThousand(kg);
  if (exact !== undefined) return quantity(exact, decimals);
  const value = Number(kg) / 1000;
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** A unit as a person reads it. The wire carries the engine's own key (`kWh_gross_cv`, `imp_gal`),
 * and a key is not a word: a record header printed `1,200 kWh_gross_cv` (journey walk, 2026-10-31).
 *
 * Presentation only - nothing here converts, and the stored unit is untouched. `kWh_gross_cv` is a
 * kWh figure stated on a gross calorific value basis (the engine's alias table treats it as kWh), so
 * the basis stays in the label rather than being dropped. An unrecognised key has its underscores
 * read as spaces, never a guess at a meaning.
 */
export function unitLabel(unit: string): string {
  switch (unit) {
    case "kWh_gross_cv":
      return "kWh (gross calorific value)";
    case "imp_gal":
      return "imperial gal";
    default:
      return unit.replace(/_/g, " ");
  }
}

/** Thousands separators on an already-rounded plain decimal, without a `Number` in sight. */
function grouped(plain: string): string {
  const negative = plain.startsWith("-");
  const [whole = "0", fraction] = (negative ? plain.slice(1) : plain).split(".");
  const head = BigInt(whole).toLocaleString("en-GB");
  return `${negative ? "-" : ""}${head}${fraction === undefined ? "" : `.${fraction}`}`;
}

/** A plain quantity with thousands separators — kWh, MMBtu, kg of refrigerant, tonnes.
 *
 * **This is where a figure is rounded, once, from the exact string the server sent** (S1-02): a
 * plain decimal goes through `roundDecimal` (half away from zero, no float), and only a string that
 * is not a plain decimal falls back to the old `Number` path. The API does not round a tonnage, so
 * a cell is never rounded twice. */
export function quantity(value: string, decimals = 0): string {
  const exact = roundDecimal(value, decimals);
  if (exact !== undefined) return grouped(exact);
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";
  return parsed.toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** An emission factor, shown at full published precision rather than rounded.
 *
 * A receipt exists so a figure can be re-performed. Rounding the factor would make that
 * impossible, so this trims only trailing zeros the publisher did not state.
 */
export function factorValue(value: string | null): string {
  if (value === null) return "—";
  return value.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
}

/** A quantity a **person typed**, shown as they meant it — neither rounded nor padded.
 *
 * `quantity(v, 0)` is right for an inventory reading in the thousands and wrong for a supplier's
 * answer: `0.5` litres renders as `1`, which is a hundred per cent out, and rule 5 permits rounding
 * in presentation but not that. And the raw string is wrong the other way — a `NUMERIC(20, 10)`
 * reaches the screen as `18400.0000000000`, which is the stored scale masquerading as precision
 * (the defect that put `4200.0000000000 tCO2e` on a public commitment panel).
 *
 * So: group the thousands, keep every digit the person actually gave, and drop only the trailing
 * zeros the column added. `factorValue` does the trimming half for the same reason.
 */
export function statedQuantity(value: string): string {
  const trimmed = factorValue(value);
  const [whole, fraction] = trimmed.split(".");
  const parsed = Number(whole);
  if (!Number.isFinite(parsed)) return "—";
  const grouped = parsed.toLocaleString("en-GB", { maximumFractionDigits: 0 });
  return fraction ? `${grouped}.${fraction}` : grouped;
}

// ------------------------------------------------------------ the one display rule (precision)

/** **How many places a figure shows, by where it is shown - the one rule** (persona review 2026-11-03,
 * item 16: "precision drifts between screens"). One quantity was printed as `1,629.9` in a table,
 * `1,629.85` on its record, `120,980 m³` as the activity and `120,979.764 m³` in the calculation, and
 * a gas line as `371.40787548` with no unit, so an auditor reconciling by eye had no way to tell a
 * rounding from a different number. The rule, and nothing else is allowed to decide it:
 *
 * - **a list or a table** shows tonnes to one place (`LIST_PLACES`);
 * - **a record or a receipt** shows tonnes to two (`RECORD_PLACES`), with the exact value as the
 *   hover text (`exactTonnes`);
 * - **an activity quantity is shown as recorded** - every digit the source gave, trailing zeros the
 *   column added dropped (`statedQuantity`) - because the activity is the input of the arithmetic
 *   and rounding an input makes the receipt's multiplication not hold;
 * - **a gas line is a mass with its unit**, to three places (`GAS_PLACES`, `gasMass`);
 * - **an intensity ratio is three significant figures** (`INTENSITY_FIGURES`, `intensity`): a ratio
 *   of `0.116` printed to a tenth is `0.1`, which is not the number.
 *
 * Every one rounds **once, from the exact string the server sent** (`roundDecimal`, scaled `BigInt`,
 * never a `Number`): a value is never rounded for one screen and rounded again for another. */
export const LIST_PLACES = 1;
export const RECORD_PLACES = 2;
export const GAS_PLACES = 3;
export const INTENSITY_FIGURES = 3;

/** Tonnes in a list or table: one place. */
export function tonnesInList(kg: string): string {
  return tonnes(kg, LIST_PLACES);
}

/** Tonnes on a record or receipt: two places. */
export function tonnesOnRecord(kg: string): string {
  return tonnes(kg, RECORD_PLACES);
}

/** The exact tonnes, every digit the server gave and no trailing zeros the column added: the hover
 * text on a rounded figure, so the rounding is a choice the reader can see through rather than a
 * different number. A dash if the value is not a decimal. */
export function exactTonnes(kg: string): string {
  const exact = perThousand(kg);
  return exact === undefined ? "—" : statedQuantity(exact);
}

/** A per-gas amount with its unit, three places: `371.408 kg CO₂e`, never a bare `371.40787548`.
 * `unit` is the unit the figure's factor states (`kg CO₂e`, or `kg CO₂` for a CO2-only factor). */
export function gasMass(kg: string, unit = "kg CO₂e"): string {
  return `${quantity(kg, GAS_PLACES)} ${unit}`;
}

/** An intensity ratio to three significant figures (`0.116`, `42.4`, `1,234`); the unit is the
 * caller's. Exact (`roundSignificant`); a dash if the value is not a decimal. */
export function intensity(value: string): string {
  const exact = roundSignificant(value, INTENSITY_FIGURES);
  return exact === undefined ? "—" : grouped(exact);
}

/** A share of a total, as a percentage string. Returns null when the total is zero, so a caller
 * shows nothing rather than "NaN%" or a misleading "0%". */
export function share(part: string, total: string): string | null {
  const t = Number(total);
  if (!Number.isFinite(t) || t === 0) return null;
  return `${((Number(part) / t) * 100).toFixed(1)}%`;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

/** An ISO date as a short human one, **without touching a timezone**.
 *
 * `new Date("2025-01-01T00:00:00Z").toLocaleDateString()` renders "31 Dec 2024" anywhere west of
 * Greenwich, because it converts an instant to local time. A reporting period is a calendar date,
 * not an instant — so this formats the string's own parts and never constructs a `Date`.
 *
 * It mattered here: a receipt was showing FY2025 as "31 Dec 2024 – 30 Dec 2025", misstating the
 * period a filed figure covers by a day at both ends.
 *
 * **The month is a name, and that is the regional half of EXP-39.** `03/10/2026` is 3 October to
 * one of this product's two markets and 10 March to the other, and neither reader has any way to
 * tell which the product meant. A named month cannot be misread, so no date this product displays
 * is ever written in digits alone — which is also why `toLocaleDateString` is wrong here even with
 * a locale: `en-GB` gives `03/10/2026` and `en-US` gives `10/3/2026`, and the screen has no way to
 * know which market is reading it.
 */
export function shortDate(iso: string | null, locale: ReaderLocale = readerLocale()): string {
  if (!iso) return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return ambiguous(iso);
  const [, year, month, day] = match;
  const name = MONTHS[Number(month) - 1];
  if (!name) return ambiguous(iso);
  // The reader's own order with the month still a name: `31 Oct 2026` or `Oct 31, 2026`. The order
  // is the whole difference, and the name is why neither can be taken for the other.
  return locale === "en-US" ? `${name} ${Number(day)}, ${year}` : `${Number(day)} ${name} ${year}`;
}

/** Something that is not an ISO date, handed back so a reader can see what arrived.
 *
 * **A slash date is marked rather than printed plain.** The passthrough used to be unconditional,
 * which is fine for `"last Tuesday"` and is the one failure this function exists to prevent for
 * `"03/10/2026"`: the screen would render it exactly as it arrived, and the formatter whose whole
 * job is to make a date unambiguous would have passed the one ambiguous shape straight through
 * looking like a formatted date.
 *
 * It says *unrecognised* rather than guessing a convention, because guessing is the defect —
 * `numbers.py` refuses an ambiguous date on the way in for the same reason, and a display layer
 * that silently settled what the parser refused to would make that refusal pointless.
 */
function ambiguous(raw: string): string {
  return /^\d{1,2}[/.]\d{1,2}[/.]\d{2,4}$/.test(raw.trim()) ? `${raw} (unrecognised date)` : raw;
}

// ------------------------------------------------------------------------ the reader (gate 30)

/** The two languages a reader may choose (`ProfilePatchIn.locale`'s closed list). */
export type ReaderLocale = "en-GB" | "en-US";

/** The locale the signed-in reader chose, **held once for every formatter in this file**.
 *
 * This is the single reader context (ADR-0199, walkthrough defect C10). `shortDate` is called from
 * some three hundred places, most of them plain functions with no hook to ask; threading a
 * parameter through each would put the same fact in a second home at every one. So there is one
 * value, written in one place (`ReaderLocaleScope`, which `RequireSession` renders from the
 * `locale` that `GET /auth/me` already returns) and read here. `undefined` - nobody is signed in,
 * or the server did not say - is **not a default reader**: dates then read day-first with a named
 * month, which cannot be taken for the other order, and nothing is guessed on anybody's behalf.
 *
 * Nothing outside this file builds a date string; `dateFormatGuard.test.ts` fails if one does,
 * which is how a call site that bypasses this cannot be missed. */
let activeLocale: ReaderLocale | undefined;

/** Called only by `ReaderLocaleScope`. */
export function setReaderLocale(locale: ReaderLocale | undefined): void {
  activeLocale = locale;
}

/** The locale dates are written in right now; day-first when none is known (see above). */
export function readerLocale(): ReaderLocale {
  return activeLocale ?? "en-GB";
}

/** What a signed-in person has chosen to read **instants** in. Two fields, and `units` is
 * deliberately not one of them.
 *
 * `units` is **not a setting** (the profile screen offers no control; ADR-0210) and is applied nowhere: every quantity this product prints is either tonnes of
 * CO2e (the unit both regimes file in) or an activity reading in the unit its source document
 * stated, and the receipt's multiplication only re-performs in that unit. Showing a litre reading
 * as gallons would put a number on screen that no document says and no receipt reproduces (rule 3).
 * That is a decision, not a formatting detail, so it is not made here - see `Profile.tsx`.
 *
 * **`locale` governs the clock and nothing else it could be mistaken for.** Both offered locales
 * group thousands with a comma and separate decimals with a point, so there is no number this file
 * writes that differs between them (the paragraph at the top of this file), and a calendar date is
 * a named month in either so it cannot be misread. What does differ is the clock: `15:05` against
 * `3:05 PM`.
 *
 * `undefined` - the profile is loading, failed, or held something unreadable - is **not a default
 * reader**. Every function below answers it with the instant as the server sent it: UTC, and
 * labelled UTC, so nothing is guessed on the reader's behalf. */
export interface Reader {
  readonly locale: ReaderLocale;
  /** An IANA zone name the engine has accepted. `readerOf` is the only place one is built. */
  readonly timeZone: string;
}

/** Whether this engine accepts the name as a time zone. Lives here because `format.ts` holds the
 * only `Intl` call in the product (`format.test.ts` asserts it). */
export function isKnownZone(name: string): boolean {
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: name });
    return true;
  } catch {
    return false;
  }
}

/** An instant as a calendar date, a time of day and a zone label, all in one zone. */
export interface Zoned {
  /** `YYYY-MM-DD` in the reader's zone - an input to `shortDate`, which names the month. */
  readonly date: string;
  readonly time: string;
  readonly zone: string;
}

/** Break an instant into the reader's local date and clock, or `null` if it is not an instant.
 *
 * `seconds` is for the audit trail, which prints them. An unsupported zone falls back to UTC rather
 * than throwing: a stored zone the engine cannot read must not blank a screen, and UTC is what the
 * server sent. */
export function zoned(iso: string, reader: Reader | undefined, seconds = false): Zoned | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const locale = reader?.locale ?? readerLocale();
  const attempt = (timeZone: string): Zoned => {
    const format = new Intl.DateTimeFormat(locale, {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      ...(seconds ? { second: "2-digit" as const } : {}),
      hourCycle: locale === "en-US" ? "h12" : "h23",
      timeZoneName: "short",
    });
    const part: Record<string, string> = {};
    for (const piece of format.formatToParts(at)) part[piece.type] = piece.value;
    const clock = seconds ? `:${part["second"] ?? "00"}` : "";
    const hour = locale === "en-US" ? (part["hour"] ?? "") : (part["hour"] ?? "").padStart(2, "0");
    const time =
      locale === "en-US"
        ? `${hour}:${part["minute"] ?? "00"}${clock} ${(part["dayPeriod"] ?? "").toUpperCase()}`
        : `${hour}:${part["minute"] ?? "00"}${clock}`;
    return {
      date: `${part["year"]}-${part["month"]}-${part["day"]}`,
      time,
      zone: timeZone === "UTC" ? "UTC" : (part["timeZoneName"] ?? timeZone),
    };
  };
  try {
    return attempt(reader?.timeZone ?? "UTC");
  } catch {
    return attempt("UTC");
  }
}

/** An instant's **calendar date in the reader's zone**, as `shortDate` writes it.
 *
 * For a field that is an instant but is shown as a day (`recorded_at`, `submitted_at`): at 02:00
 * UTC on 18 October it is still the 17th in California, and a reader looking for "what happened
 * yesterday" is wrong by a day in the direction nobody checks. Without a reader this is exactly
 * `shortDate` - the date in the string the server sent. A *calendar date* (a period start, an
 * expiry) must never come through here: `shortDate` is correct for it because it names a day, not
 * a moment. */
export function instantDate(iso: string | null, reader?: Reader): string {
  if (!iso) return "—";
  const parts = reader ? zoned(iso, reader) : null;
  return shortDate(parts ? parts.date : iso, reader?.locale);
}

/** An instant's time of day with its zone: `15:05 UTC`, `08:05 GMT-7`, `8:05 AM PDT`.
 *
 * `null` for something that is not an instant, so a caller shows the date alone rather than
 * "Invalid Date". The zone is always said: a time with no zone is the ambiguity `shortDate` exists
 * to refuse for dates. */
export function instantTime(iso: string, reader?: Reader, seconds = false): string | null {
  const parts = zoned(iso, reader, seconds);
  return parts ? `${parts.time} ${parts.zone}` : null;
}

/** An instant as `3 Oct 2026, 15:05 UTC`: date and time **from one zone**, the zone said.
 *
 * The date and the time are taken from the same zone on purpose - `11:30 PM PDT` printed beside the
 * UTC date would name tomorrow. Without a reader it is UTC, labelled UTC. Something that is not an
 * instant comes back as it arrived rather than being made up. */
export function instantStamp(iso: string, reader?: Reader): string {
  const parts = zoned(iso, reader);
  if (parts === null) return iso;
  return `${shortDate(parts.date, reader?.locale)}, ${parts.time} ${parts.zone}`;
}

/** A `YYYY-MM` as a named month: "April 2025".
 *
 * The register's month filter and its chip. `2025-04` is not ambiguous — it is ISO — but it is the
 * one value a reader has to decode in a control they are choosing from, and the same rule applies:
 * a month this product shows is a name.
 */
export function monthLabel(yearMonth: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(yearMonth);
  if (!match) return yearMonth;
  const [, year, month] = match;
  return MONTH_NAMES[Number(month) - 1] ? `${MONTH_NAMES[Number(month) - 1]} ${year}` : yearMonth;
}

/** A `YYYY-MM` as a short named month: "Apr 2025". For an axis, where "April 2025" does not fit.
 *
 * The same rule as `monthLabel`: a month this product shows is a name, and it is written from the
 * string's own parts so no timezone is involved. A chart axis passes the tick's `Date` through
 * `iso.slice(0, 7)` rather than calling a locale formatter of its own, which is the only way this
 * file stays the one place that formats anything. */
export function shortMonthYear(yearMonth: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(yearMonth);
  const name = match ? MONTHS[Number(match[2]) - 1] : undefined;
  return match && name ? `${name} ${match[1]}` : yearMonth;
}

/** A period for a table cell, short: "Jan 2025" for one whole month, "Jan–Dec 2025" for whole months of one
 * year, and two short dates otherwise (a part-month, or a span across two years). Written from the ISO
 * strings' own parts, so no time zone is involved. The long form (`shortDate` twice) belongs in the cell's
 * title and the export. */
export function periodShort(start: string, end: string): string {
  const full = `${shortDate(start)} – ${shortDate(end)}`;
  if (!/^\d{4}-\d{2}-01/.test(start) || start.slice(0, 4) !== end.slice(0, 4)) return full;
  const lastDay = new Date(Date.UTC(Number(end.slice(0, 4)), Number(end.slice(5, 7)), 0)).getUTCDate();
  if (Number(end.slice(8, 10)) !== lastDay) return full;
  if (start.slice(0, 7) === end.slice(0, 7)) return shortMonthYear(start.slice(0, 7));
  // "Jan 2025" and "Dec 2025" -> "Jan–Dec 2025": the year is said once.
  return `${shortMonthYear(start.slice(0, 7)).replace(/\s+\d{4}$/, "")}–${shortMonthYear(end.slice(0, 7))}`;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

/** A hash, abbreviated for display. Always shows both ends: an auditor comparing two hashes
 * checks the start and the finish, and a single truncated prefix can collide by eye. */
export function shortHash(hash: string): string {
  return hash.length <= 20 ? hash : `${hash.slice(0, 10)}…${hash.slice(-6)}`;
}

/** Sum a list of decimal strings for display - **of the rows a screen is showing, and nothing else**.
 *
 * Exact, in scaled `BigInt` (`decimal.ts`), not in JavaScript's binary float: the sum of `0.1` and
 * `0.2` is `0.3` here and `0.30000000000000004` in a `number` (rule 5). It used to be float
 * addition (whole-product review 2026-11, finding 8), and the headline total on Home, Dashboards,
 * Readiness and the register was a sum of this kind - a second figure that could differ from the
 * server's in the last printed digit. **A total the server states is not summed here**: the
 * inventory carries `total_kgco2e`, and a screen that prints a headline reads that. What is left for
 * this function is a sum over a filtered list, which the server cannot know, labelled as such.
 *
 * A value that is not a decimal makes the sum `NaN`-shaped on purpose ("unreadable is not zero"):
 * it returns `"NaN"`, which `tonnes` prints as a dash.
 */
export function sumForDisplay(values: readonly string[]): string {
  return sumDecimals(values) ?? "NaN";
}

/** A file's size in the product's own unit, **rounded up** to one decimal.
 *
 * Up, never to nearest, and that is the whole reason this is not `toFixed(1)`. It is printed beside
 * a limit the server states in whole mebibytes ("the limit is 4 MB"), in a sentence that refuses a
 * file for being over it — and `4.04` rounded to nearest is `4.0`, so the refusal would read *"this
 * file is 4.0 MB and the limit is 4 MB"*, a refusal that appears to contradict itself. Rounding up
 * keeps the one property the sentence needs: a refused file never prints as no larger than the
 * limit.
 *
 * `MB` here means 1,048,576 bytes, as it does in every sentence the server writes about this limit
 * (`validation.limit_message`). A size the OS file manager shows in decimal megabytes will differ
 * by a few percent, which is the same few percent in the server's own refusals.
 *
 * Bytes are a count, not an amount (rule 5 is about quantities that are filed), so this takes a
 * `number`: `File.size` is one.
 */
export function fileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  const megabytes = Math.ceil((bytes / (1024 * 1024)) * 10) / 10;
  return `${megabytes.toLocaleString("en-GB", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`;
}

/** The characters a reader of `locale` uses to begin a fraction and to group thousands, from the
 * platform's own number formatting - for **reading** what a person typed (`lib/decimalInput.ts`), never
 * for printing (every printed number goes through the functions above).
 *
 * It lives here because this file is the product's one home for `Intl`, and a number parser that named
 * a locale or called `Intl` itself would be a second place that chooses an order for the reader. A tag
 * the platform does not recognise falls back to a neutral language, `en`, rather than throwing in the
 * middle of a form; `group` is empty for a locale that groups with a space. */
export function numberSeparators(locale: string | undefined): { decimal: string; group: string } {
  let parts: Intl.NumberFormatPart[];
  try {
    parts = new Intl.NumberFormat(locale ?? "en").formatToParts(12345.6);
  } catch {
    parts = new Intl.NumberFormat("en").formatToParts(12345.6);
  }
  const decimal = parts.find((part) => part.type === "decimal")?.value ?? ".";
  const group = parts.find((part) => part.type === "group")?.value ?? ",";
  return { decimal, group: /^[\s\u00a0\u202f]*$/.test(group) ? "" : group };
}
